import React from "react"
import { useDrag } from "react-dnd"
import { BiDotsVerticalRounded } from "react-icons/bi"
import { IoIosWarning } from "react-icons/io"
import {
	startTimeAllCases,
	endTimeAllCases,
	currentlyRealTimeCompleted,
	confirmedCancellationNoShow
} from "../../../utils/realTimeUtils"
import { timeStringToMinutes } from "../../../utils/generalUtils"
import CustomTooltipContent from "../tooltip"
import Tooltip from "../../Tooltip"

const ItemType = "TASK"

const getDurationStyleIndex = length => {
	if (length >= 0 && length <= 119) return 0
	if (length >= 120 && length <= 179) return 1
	if (length >= 180 && length <= 299) return 2
	if (length >= 300 && length <= 359) return 3
	if (length >= 360 && length <= 479) return 4
	if (length >= 480) return 5
	return 0 // Default case if none match
}

export const DraggableTask = React.memo(
	({
		task,
		editMode,
		onClick,
		viewType,
		color,
		colorsBackground,
		colorsText,
		view,
		heightPerTask,
		sandbox,
		isTomorrowMode,
		resourceId,
		nurseSchedule,
		chairSchedule,
		currentSchedule
	}) => {
		const { startTime, endTime } = {
			startTime: startTimeAllCases(task, sandbox, isTomorrowMode),
			endTime: endTimeAllCases(task, sandbox, isTomorrowMode)
		}

		const [{ isDragging }, drag] = useDrag(
			() => ({
				type: ItemType,
				item: { task, startX: startTime, startY: task.row },
				collect: monitor => ({ isDragging: monitor.isDragging() }),
				canDrag: () => editMode
			}),
			[editMode]
		)

		const isDetailedView = view === "Detailed"
		const propertyCount = isDetailedView || view !== "Minimal" ? 2 : 1
		const taskHeight = 30 * propertyCount
		const openTime = timeStringToMinutes(currentSchedule.openTime)
		const closeTime = timeStringToMinutes(currentSchedule.closeTime)
		const totalMinutes = closeTime - openTime

		let taskWidth = `${((endTime - startTime) * 100) / totalMinutes}%`
		const topPosition = `${task.row * heightPerTask}px`
		const leftPosition = `${((startTime - openTime) * 100) / totalMinutes}%`
		const isGrayedOut =
			sandbox || isTomorrowMode
				? confirmedCancellationNoShow(task)
				: currentlyRealTimeCompleted(task) || confirmedCancellationNoShow(task)

		// Calculate overflow
		const totalPosition =
			parseFloat(taskWidth.replace("%", "")) + parseFloat(leftPosition.replace("%", ""))
		const overflow = totalPosition > 100
		if (overflow) {
			taskWidth = `${100 - parseFloat(leftPosition)}%`
		}

		const taskStyle = {
			left: leftPosition,
			width: taskWidth,
			height: `${taskHeight}px`,
			top: topPosition,
			zIndex: isDragging ? 10 : 1,
			cursor: editMode ? (isDragging ? "grabbing" : "grab") : "auto",
			border: confirmedCancellationNoShow(task) ? "2px solid #aaaaaa" : "none"
		}

		// Get the secondary detail text based on viewType
		const getSecondaryText = () => {
			if (viewType === "nurse") {
				const chairName =
					chairSchedule.find(chair => chair.chairId === task.assignedChair)?.chairName ||
					"Unknown Chair"
				return `${task.infusionType} (${chairName})`
			} else {
				const nurseName =
					nurseSchedule.find(nurse => nurse.nurseId === task.assignedNurse)?.nurseName ||
					"Unknown Nurse"
				return `${task.infusionType} (${nurseName})`
			}
		}

		return (
			<Tooltip
				content={
					<CustomTooltipContent
						task={task}
						position={{
							left: leftPosition,
							width: taskWidth,
							height: taskHeight,
							top: topPosition
						}}
					/>
				}
				editMode={editMode}
			>
				<div
					ref={editMode ? drag : null}
					onClick={onClick}
					className={`task-bar ${
						confirmedCancellationNoShow(task)
							? "bg-gray-300 opacity-75"
							: isGrayedOut
								? "bg-gray-400"
								: color === "Acuity"
									? colorsBackground[task.acuity] || "bg-gray-200"
									: color === "Duration"
										? colorsBackground[getDurationStyleIndex(task.length)] ||
											"bg-gray-200"
										: "bg-gray-200"
					} flex items-center absolute transition-transform duration-100 ease-in-out shadow-lg rounded-md px-2`}
					style={taskStyle}
				>
					{editMode && (
						<div className="grab-handle cursor-grab w-4 h-full flex items-center justify-center">
							<BiDotsVerticalRounded className="text-white text-lg" />
						</div>
					)}

					<p
						className={`sensitive flex-none ml-0.5 mr-1 text-xs w-5 h-5 
							flex items-center justify-center rounded-full font-bold text-sm bg-white 
							${
								confirmedCancellationNoShow(task)
									? "text-gray-400"
									: isGrayedOut
										? "text-gray-400"
										: color === "Acuity"
											? colorsText[task.acuity] || "text-gray-200"
											: color === "Duration"
												? colorsText[getDurationStyleIndex(task.length)] ||
													"text-gray-200"
												: "text-gray-200"
							}`}
					>
						{typeof task.patientName === "string"
							? task.patientName
									.split(" ")
									.map(item => item[0])
									.join("")
							: String(task.patientName)}
					</p>

					<div
						className={`text-left text-sm overflow-hidden text-ellipsis whitespace-nowrap w-full 
							${confirmedCancellationNoShow(task) ? "text-gray-400" : "text-white"}`}
					>
						<span
							className={`sensitive ${isDetailedView ? "font-bold" : ""} block truncate`}
						>
							{task.patientName}
						</span>
						{isDetailedView && (
							<span className="block truncate">{getSecondaryText()}</span>
						)}
					</div>

					{/* Render a warning symbol if there is overflow */}
					{overflow && (
						<div className="flex items-center text-white">
							<span>OT </span>
							<IoIosWarning className="ml-2 text-white text-lg" />
						</div>
					)}
				</div>
			</Tooltip>
		)
	}
)
